import React, { useState, useRef, useEffect } from 'react';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { Button, Row, Col, Form as BootstrapForm, Spinner } from 'react-bootstrap';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import 'react-quill/dist/quill.snow.css';
import ReactQuill from 'react-quill';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import './BlogForm.css';

const BlogForm = ({ newsId = null, isEditMode = false, initialValues = null }) => {
    const [contentEn, setContentEn] = useState('');
    const [contentAr, setContentAr] = useState('');
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const quillRef = useRef(null);
    const navigate = useNavigate();

    const defaultInitialValues = {
        featuredImage: null,
        titleEn: '',
        titleAr: '',
    };

    const validationSchema = Yup.object().shape({
        featuredImage: isEditMode ? 
            Yup.mixed() : 
            Yup.mixed().required('Featured Image is required'),
        titleEn: Yup.string().required('News Title (En) is required').max(255, 'Title must be 255 characters or less'),
        titleAr: Yup.string().required('News Title (Ar) is required').max(255, 'Title must be 255 characters or less'),
    });

    // Set initial values from props or fetch data for editing
    useEffect(() => {
        if (isEditMode && newsId) {
            if (initialValues) {
                // Use the data passed from parent component
                setContentEn(initialValues.content_en || '');
                setContentAr(initialValues.content_ar || '');
                setLoading(false);
            } else {
                // Fetch data if not provided
                fetchNewsData();
            }
        }
    }, [isEditMode, newsId, initialValues]);

    const fetchNewsData = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`https://api.admin-panel.nhcholding.com/api/news/${newsId}/edit`, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            });
            const newsData = response.data.news; // Extract news from response
            
            // Set form initial values
            setContentEn(newsData.content_en || '');
            setContentAr(newsData.content_ar || '');
        } catch (error) {
            console.error('Error fetching news data:', error);
            toast.error('Failed to load news data');
            navigate('/cms/blog-management');
        } finally {
            setLoading(false);
        }
    };

    const getInitialValues = () => {
        if (initialValues) {
            return {
                featuredImage: null,
                titleEn: initialValues.title_en || '',
                titleAr: initialValues.title_ar || '',
            };
        }
        return defaultInitialValues;
    };

    const handleFormSubmit = async (values, { resetForm }) => {
        // Validate content
        if (!contentEn.trim() || contentEn === '<p><br></p>') {
            toast.error('English content is required');
            return;
        }
        
        if (!contentAr.trim() || contentAr === '<p><br></p>') {
            toast.error('Arabic content is required');
            return;
        }

        setSubmitting(true);
        
        try {
            const formData = new FormData();
            formData.append('titleEn', values.titleEn);
            formData.append('titleAr', values.titleAr);
            formData.append('contentEn', contentEn);
            formData.append('contentAr', contentAr);
            
            if (values.featuredImage) {
                formData.append('featuredImage', values.featuredImage);
            }

            let response;
            if (isEditMode && newsId) {
                // Update existing news
                response = await axios.post(`https://api.admin-panel.nhcholding.com/api/news/${newsId}`, formData, {
                    headers: { 
                        Authorization: `Bearer ${localStorage.getItem('token')}`,
                        'Content-Type': 'multipart/form-data'
                    }
                });
                toast.success('News updated successfully!');
            } else {
                // Create new news
                response = await axios.post('https://api.admin-panel.nhcholding.com/api/news', formData, {
                    headers: { 
                        Authorization: `Bearer ${localStorage.getItem('token')}`,
                        'Content-Type': 'multipart/form-data'
                    }
                });
                toast.success('News created successfully!');
                resetForm();
                setContentEn('');
                setContentAr('');
            }

            // Navigate back to listing page after success
            setTimeout(() => {
                navigate('/cms/blog-management');
            }, 1500);

        } catch (error) {
            console.error('Error submitting news:', error);
            
            // Handle validation errors
            if (error.response && error.response.data && error.response.data.errors) {
                const errors = error.response.data.errors;
                Object.keys(errors).forEach(key => {
                    errors[key].forEach(message => {
                        toast.error(message);
                    });
                });
            } else {
                toast.error(isEditMode ? 'Failed to update news' : 'Failed to create news');
            }
        } finally {
            setSubmitting(false);
        }
    };

    const modules = {
        toolbar: [
            [{ 'header': '1' }, { 'header': '2' }, { 'font': [] }],
            [{ size: [] }],
            ['bold', 'italic', 'underline', 'strike', 'blockquote'],
            [{ 'list': 'ordered' }, { 'list': 'bullet' }],
            ['link', 'image'],
            [{ 'align': [] }],
            [{ 'color': [] }, { 'background': [] }],
            ['clean'],
        ],
    };

    if (loading) {
        return (
            <div className="text-center p-5">
                <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                </Spinner>
                <p>Loading news data...</p>
            </div>
        );
    }

    return (
        <div className="blog-form">
            <ToastContainer />
            <Formik
                initialValues={getInitialValues()}
                validationSchema={validationSchema}
                onSubmit={handleFormSubmit}
                enableReinitialize={true}
            >
                {({ setFieldValue, errors, touched, handleSubmit }) => (
                    <Form>
                        {/* Featured Image Section */}
                        <div className="form-section">
                            <h5 className="section-title">Featured Image</h5>
                            <Row className="mb-4">
                                <Col md={12}>
                                    <BootstrapForm.Group className="image-upload-container">
                                        <BootstrapForm.Label className="form-label-styled">
                                            Upload Featured Image 
                                            {!isEditMode && <span className="required-mark">*</span>}
                                            <small className="text-muted d-block">Maximum file size: 2MB. Supported formats: JPG, PNG, JPEG</small>
                                        </BootstrapForm.Label>
                                        <input
                                            type="file"
                                            className="form-control file-input"
                                            accept="image/*"
                                            onChange={(e) => setFieldValue('featuredImage', e.target.files[0])}
                                        />
                                        {isEditMode && (
                                            <small className="text-info mt-2 d-block">
                                                <i className="bi bi-info-circle me-1"></i>
                                                Leave empty to keep current image
                                            </small>
                                        )}
                                        {touched.featuredImage && errors.featuredImage && (
                                            <div className="error-message">{errors.featuredImage}</div>
                                        )}
                                    </BootstrapForm.Group>
                                </Col>
                            </Row>
                        </div>

                        {/* Title Section */}
                        <div className="form-section">
                            <h5 className="section-title">News Titles</h5>
                            <Row className="mb-4">
                                <Col md={6}>
                                    <BootstrapForm.Group className="input-group-styled">
                                        <BootstrapForm.Label className="form-label-styled">
                                            News Title (English) <span className="required-mark">*</span>
                                        </BootstrapForm.Label>
                                        <Field
                                            type="text"
                                            name="titleEn"
                                            placeholder="Enter news title in English"
                                            className="form-control styled-input"
                                        />
                                        {touched.titleEn && errors.titleEn && (
                                            <div className="error-message">{errors.titleEn}</div>
                                        )}
                                    </BootstrapForm.Group>
                                </Col>
                                <Col md={6}>
                                    <BootstrapForm.Group className="input-group-styled">
                                        <BootstrapForm.Label className="form-label-styled">
                                            News Title (Arabic) <span className="required-mark">*</span>
                                        </BootstrapForm.Label>
                                        <Field
                                            type="text"
                                            name="titleAr"
                                            placeholder="أدخل عنوان الخبر باللغة العربية"
                                            className="form-control styled-input"
                                            dir="rtl"
                                        />
                                        {touched.titleAr && errors.titleAr && (
                                            <div className="error-message">{errors.titleAr}</div>
                                        )}
                                    </BootstrapForm.Group>
                                </Col>
                            </Row>
                        </div>

                        {/* Content Section */}
                        <div className="form-section">
                            <h5 className="section-title">Content</h5>
                            <Row className="mb-4">
                                <Col md={12}>
                                    <BootstrapForm.Group className="editor-group">
                                        <BootstrapForm.Label className="form-label-styled">
                                            Content (English) <span className="required-mark">*</span>
                                        </BootstrapForm.Label>
                                        <div className="editor-container">
                                            <ReactQuill
                                                ref={quillRef}
                                                theme="snow"
                                                value={contentEn}
                                                onChange={(value) => setContentEn(value)}
                                                modules={modules}
                                                placeholder="Write your news content in English here..."
                                                className="styled-editor"
                                            />
                                        </div>
                                    </BootstrapForm.Group>
                                </Col>
                            </Row>
                            <Row className="mb-4">
                                <Col md={12}>
                                    <BootstrapForm.Group className="editor-group">
                                        <BootstrapForm.Label className="form-label-styled">
                                            Content (Arabic) <span className="required-mark">*</span>
                                        </BootstrapForm.Label>
                                        <div className="editor-container">
                                            <ReactQuill
                                                ref={quillRef}
                                                theme="snow"
                                                value={contentAr}
                                                onChange={(value) => setContentAr(value)}
                                                modules={modules}
                                                placeholder="اكتب محتوى الخبر باللغة العربية هنا..."
                                                className="styled-editor arabic-editor"
                                            />
                                        </div>
                                    </BootstrapForm.Group>
                                </Col>
                            </Row>
                        </div>

                        {/* Action Buttons */}
                        <div className="form-actions">
                            <Button
                                variant="outline-secondary"
                                className="btn-cancel"
                                onClick={() => navigate('/cms/blog-management')}
                                disabled={submitting}
                            >
                                <i className="bi bi-x-circle me-2"></i>
                                Cancel
                            </Button>
                            <Button
                                variant="primary"
                                className="btn-submit"
                                onClick={handleSubmit}
                                disabled={submitting}
                            >
                                {submitting ? (
                                    <>
                                        <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                                        {isEditMode ? 'Updating...' : 'Creating...'}
                                    </>
                                ) : (
                                    <>
                                        <i className={`bi ${isEditMode ? 'bi-pencil-square' : 'bi-plus-circle'} me-2`}></i>
                                        {isEditMode ? 'Update News' : 'Create News'}
                                    </>
                                )}
                            </Button>
                        </div>
                    </Form>
                )}
            </Formik>
        </div>
    );
};

export default BlogForm;